import type { SheetRows } from './sheetReader';
import {
  buildMigratedFinanceData,
  buildMigratedHandData,
  handEmiForMigration,
  type MigratedFinanceInput,
  type MigratedHandInput,
} from './migrationRecords';
import type { MigratedSchedulePreview } from './migrationSchedule';

/** Columns of the bulk migration sheet. `aliases` are other headings accepted for the same column. */
export const BULK_COLUMNS = [
  { key: 'type', header: 'Loan type (finance / hand)', aliases: ['type', 'loan type', 'kind'] },
  { key: 'customerName', header: 'Customer name', aliases: ['name', 'borrower', 'borrower name', 'customer'] },
  { key: 'mobile', header: 'Mobile', aliases: ['mobile number', 'phone', 'contact'] },
  { key: 'cutoverDate', header: 'Cutover date (optional)', aliases: ['cutover date', 'cutover'] },
  { key: 'outstanding', header: 'Outstanding principal', aliases: ['outstanding', 'principal outstanding', 'balance principal'] },
  { key: 'emiAmount', header: 'EMI amount (finance)', aliases: ['emi', 'emi amount'] },
  { key: 'remainingEmis', header: 'EMIs still to collect incl. overdue', aliases: ['emis still to collect', 'remaining emis', 'emis remaining', 'balance emis'] },
  { key: 'overdueEmis', header: 'EMIs overdue at cutover', aliases: ['overdue emis', 'emis overdue', 'overdue'] },
  { key: 'nextDueDate', header: 'Next due date', aliases: ['next emi due date', 'due date', 'repayment due date'] },
  { key: 'interestRate', header: 'Interest (Rs per 100 per month)', aliases: ['interest', 'interest rate', 'rate'] },
  { key: 'interestDue', header: 'Interest or charges due at cutover', aliases: ['interest due', 'interest due at cutover', 'other charges'] },
  { key: 'originalAgreementDate', header: 'Original agreement date', aliases: ['agreement date'] },
  { key: 'originalPrincipal', header: 'Original principal', aliases: ['loan amount', 'original loan amount'] },
  { key: 'originalTenureMonths', header: 'Original tenure months', aliases: ['tenure', 'tenure months'] },
  { key: 'emisPaidBefore', header: 'EMIs paid before cutover', aliases: ['emis paid', 'paid emis'] },
  { key: 'haNumber', header: 'HA number', aliases: ['ha no', 'agreement number', 'loan number'] },
  { key: 'regNo', header: 'Vehicle reg no', aliases: ['reg no', 'registration number', 'vehicle number'] },
  { key: 'repayment', header: 'Hand loan repayment (emi / short_term)', aliases: ['repayment', 'repayment type'] },
  { key: 'interestType', header: 'Hand loan interest type (simple / compound)', aliases: ['interest type'] },
  { key: 'handLoanType', header: 'Hand loan purpose (personal / vehicle)', aliases: ['hand loan type', 'purpose'] },
  { key: 'photoFile', header: 'Photo file name (optional)', aliases: ['photo', 'photo file', 'photo name'] },
  { key: 'notes', header: 'Notes', aliases: ['remarks', 'note'] },
] as const;

export type BulkColumnKey = (typeof BULK_COLUMNS)[number]['key'];
export type BulkCells = Partial<Record<BulkColumnKey, string>>;

const normHeader = (s: string) => String(s || '').toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };

const pad = (n: number) => String(n).padStart(2, '0');

function validYmd(y: number, m: number, d: number): string | null {
  if (y < 1900 || y > 2200 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

/**
 * Accepts 2026-10-15, 15-10-2026, 15/10/2026, 15.10.2026, 15-Oct-2026, 15 Oct 2026 and Excel
 * date serials. Day/month order is Indian (day first) unless the first part can't be a month.
 * Returns '' for blank, null when the value can't be understood.
 */
export function parseSheetDate(raw: string): string | null {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/^\d+(\.\d+)?$/.test(s)) {
    const serial = Math.floor(Number(s));
    if (serial < 20000 || serial > 80000) return null;
    const dt = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
    return validYmd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  }
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return validYmd(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[-/. ]([A-Za-z]{3,9})[-/. ,]+(\d{2,4})$/);
  if (m) {
    const mon = MONTHS[m[2].slice(0, 4).toLowerCase()] ?? MONTHS[m[2].slice(0, 3).toLowerCase()];
    const y = +m[3] < 100 ? 2000 + +m[3] : +m[3];
    return mon ? validYmd(y, mon, +m[1]) : null;
  }
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (m) {
    let d = +m[1];
    let mo = +m[2];
    const y = +m[3] < 100 ? 2000 + +m[3] : +m[3];
    if (mo > 12 && d <= 12) [d, mo] = [mo, d];
    return validYmd(y, mo, d);
  }
  return null;
}

const cleanNumber = (raw: string | undefined) => String(raw ?? '').replace(/[₹,\s]/g, '').replace(/^rs\.?/i, '');

export type BulkRowStatus = 'ready' | 'error' | 'duplicate' | 'skipped';

export type BulkRow = {
  rowNumber: number;
  kind: 'finance' | 'handloan' | null;
  cells: BulkCells;
  status: BulkRowStatus;
  message: string;
  name: string;
  mobile: string;
  outstanding: number;
  emi: number;
  cutoverYmd: string;
  nextDueYmd: string;
  overdueCount: number;
  overdueAmount: number;
  oldestOverdueDays: number;
  photoKey: string;
  dedupeKey: string;
  financeInput?: MigratedFinanceInput;
  handInput?: MigratedHandInput;
};

function parseKind(raw: string | undefined): 'finance' | 'handloan' | null {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (s.startsWith('f') || s.includes('vehicle finance')) return 'finance';
  if (s.startsWith('h') || s.startsWith('p') || s.includes('hand')) return 'handloan';
  return null;
}

const digits = (s: string) => String(s || '').replace(/\D/g, '');

export function financeDedupeKey(d: Record<string, unknown>): string {
  const ha = String(d.haNumber ?? '').trim().toLowerCase();
  if (ha) return `fin:ha:${ha}`;
  return `fin:${digits(String(d.mobileNumber ?? ''))}:${String(d.customerName ?? '').trim().toLowerCase()}:${Math.round(Number(d.openingPrincipalOutstanding ?? d.totalAmount) || 0)}`;
}

export function handDedupeKey(d: Record<string, unknown>): string {
  return `hand:${digits(String(d.suretyNumber ?? ''))}:${String(d.name ?? '').trim().toLowerCase()}:${Math.round(Number(d.openingPrincipalOutstanding ?? d.loanAmount) || 0)}`;
}

/** Maps sheet rows (first row = headings) to cells keyed by column. Unknown headings are ignored. */
export function mapSheet(rows: SheetRows): { cells: BulkCells[]; rowNumbers: number[]; missing: string[] } {
  if (!rows.length) return { cells: [], rowNumbers: [], missing: ['Customer name', 'Outstanding principal'] };
  const header = rows[0].map(normHeader);
  const colFor = new Map<BulkColumnKey, number>();
  for (const col of BULK_COLUMNS) {
    const names = [col.header, ...col.aliases].map(normHeader);
    const idx = header.findIndex((h) => h && names.includes(h));
    if (idx >= 0) colFor.set(col.key, idx);
  }
  const required: BulkColumnKey[] = ['type', 'customerName', 'outstanding', 'nextDueDate'];
  const missing = required.filter((k) => !colFor.has(k)).map((k) => BULK_COLUMNS.find((c) => c.key === k)!.header);
  const cells: BulkCells[] = [];
  const rowNumbers: number[] = [];
  rows.slice(1).forEach((r, i) => {
    const c: BulkCells = {};
    colFor.forEach((idx, key) => {
      c[key] = String(r[idx] ?? '').trim();
    });
    cells.push(c);
    rowNumbers.push(i + 2);
  });
  return { cells, rowNumbers, missing };
}

/**
 * Validates every row with the same rules as the single-loan migration forms and flags
 * duplicates (already in the software, or repeated in the sheet).
 */
export function analyzeBulkRows(
  cells: BulkCells[],
  rowNumbers: number[],
  opts: { defaultCutoverYmd: string; existingKeys: Set<string> }
): BulkRow[] {
  const seen = new Set<string>();
  return cells.map((c, i) => {
    const kind = parseKind(c.type);
    const name = String(c.customerName ?? '').trim();
    const mobile = String(c.mobile ?? '').trim();
    const base: BulkRow = {
      rowNumber: rowNumbers[i],
      kind,
      cells: c,
      status: 'error',
      message: '',
      name,
      mobile,
      outstanding: Number(cleanNumber(c.outstanding)) || 0,
      emi: 0,
      cutoverYmd: '',
      nextDueYmd: '',
      overdueCount: 0,
      overdueAmount: 0,
      oldestOverdueDays: 0,
      photoKey: String(c.photoFile ?? '').trim(),
      dedupeKey: '',
    };
    if (/^example\b/i.test(name)) return { ...base, status: 'skipped', message: 'Example row — not imported.' };
    if (!kind) return { ...base, message: 'Loan type must be "finance" or "hand".' };

    const cutover = c.cutoverDate ? parseSheetDate(c.cutoverDate) : opts.defaultCutoverYmd;
    if (!cutover) return { ...base, message: `Cutover date "${c.cutoverDate}" is not a date.` };
    const nextDue = parseSheetDate(c.nextDueDate ?? '');
    if (nextDue === null) return { ...base, cutoverYmd: cutover, message: `Next due date "${c.nextDueDate}" is not a date.` };
    const agreement = parseSheetDate(c.originalAgreementDate ?? '');
    if (agreement === null) return { ...base, cutoverYmd: cutover, message: `Original agreement date "${c.originalAgreementDate}" is not a date.` };

    let result;
    let financeInput: MigratedFinanceInput | undefined;
    let handInput: MigratedHandInput | undefined;
    if (kind === 'finance') {
      financeInput = {
        cutoverYmd: cutover,
        customerName: name,
        mobileNumber: mobile,
        regNo: c.regNo,
        haNumber: c.haNumber,
        originalAgreementDate: agreement,
        originalPrincipal: cleanNumber(c.originalPrincipal),
        originalTenureMonths: cleanNumber(c.originalTenureMonths),
        emisPaidBefore: cleanNumber(c.emisPaidBefore),
        outstandingPrincipal: cleanNumber(c.outstanding),
        interestDue: cleanNumber(c.interestDue),
        remainingEmis: cleanNumber(c.remainingEmis),
        overdueEmis: cleanNumber(c.overdueEmis),
        emiAmount: cleanNumber(c.emiAmount),
        interestPercentage: cleanNumber(c.interestRate),
        nextDueDate: nextDue,
        notes: c.notes,
      };
      result = buildMigratedFinanceData(financeInput);
    } else {
      const repayment = String(c.repayment ?? '').toLowerCase().replace(/[^a-z]/g, '');
      handInput = {
        cutoverYmd: cutover,
        name,
        mobile,
        loanType: c.handLoanType,
        repaymentType: repayment.startsWith('short') || repayment === 'lumpsum' ? 'short_term' : 'emi',
        interestType: String(c.interestType ?? '').toLowerCase().startsWith('c') ? 'compound' : 'simple',
        interestRate: cleanNumber(c.interestRate),
        originalAgreementDate: agreement,
        originalPrincipal: cleanNumber(c.originalPrincipal),
        outstandingPrincipal: cleanNumber(c.outstanding),
        openingInterestDue: cleanNumber(c.interestDue),
        remainingEmis: cleanNumber(c.remainingEmis),
        emisPaidBefore: cleanNumber(c.emisPaidBefore),
        overdueEmis: cleanNumber(c.overdueEmis),
        nextDueDate: nextDue,
        notes: c.notes,
      };
      result = buildMigratedHandData(handInput);
    }

    const filled = { ...base, cutoverYmd: cutover, nextDueYmd: nextDue, financeInput, handInput };
    if (!result.ok) return { ...filled, message: result.error };

    const preview: MigratedSchedulePreview | null = result.preview;
    const emi =
      kind === 'finance'
        ? Number(result.data.emiAmount) || 0
        : handInput?.repaymentType === 'emi'
          ? handEmiForMigration(handInput)
          : 0;
    const dedupeKey = kind === 'finance' ? financeDedupeKey(result.data) : handDedupeKey(result.data);
    const summary = {
      ...filled,
      emi,
      overdueCount: result.overdueAtCutover,
      overdueAmount: preview?.overdueAmount ?? 0,
      oldestOverdueDays: preview?.oldestDays ?? 0,
      dedupeKey,
    };
    if (opts.existingKeys.has(dedupeKey)) return { ...summary, status: 'duplicate', message: 'Already in the software — skipped.' };
    if (seen.has(dedupeKey)) return { ...summary, status: 'duplicate', message: 'Repeated in this sheet — skipped.' };
    seen.add(dedupeKey);
    return { ...summary, status: 'ready', message: result.overdueAtCutover ? `${result.overdueAtCutover} EMI(s) overdue at cutover` : 'Up to date' };
  });
}

/** Normalised lookup keys a photo file can be matched by: its name without extension. */
export const photoFileKey = (fileName: string) => String(fileName || '').trim().toLowerCase().replace(/\.[a-z0-9]+$/, '');

/** Photo for a row: the "Photo file name" column first, then a file named after the HA number or mobile. */
export function matchRowPhoto<T>(row: BulkRow, photos: Map<string, T>): T | undefined {
  const candidates = [photoFileKey(row.photoKey), String(row.cells.haNumber ?? '').trim().toLowerCase(), digits(row.mobile)];
  for (const k of candidates) {
    if (k && photos.has(k)) return photos.get(k);
  }
  return undefined;
}

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** Template with headings and two example rows (rows whose name starts with "EXAMPLE" are never imported). */
export function bulkTemplateCsv(): string {
  const header = BULK_COLUMNS.map((c) => c.header);
  const example = (vals: Partial<Record<BulkColumnKey, string>>) => BULK_COLUMNS.map((c) => vals[c.key] ?? '');
  const rows = [
    header,
    example({
      type: 'finance',
      customerName: 'EXAMPLE Ravi Kumar',
      mobile: '9000000001',
      outstanding: '85000',
      emiAmount: '8500',
      remainingEmis: '12',
      overdueEmis: '2',
      nextDueDate: '10-11-2026',
      interestRate: '1.5',
      originalAgreementDate: '10-02-2026',
      originalPrincipal: '120000',
      originalTenureMonths: '18',
      emisPaidBefore: '6',
      haNumber: 'HA-EX-001',
      regNo: 'AP00XX0000',
      photoFile: 'HA-EX-001.jpg',
    }),
    example({
      type: 'hand',
      customerName: 'EXAMPLE Lakshmi',
      mobile: '9000000002',
      outstanding: '40000',
      remainingEmis: '8',
      overdueEmis: '1',
      nextDueDate: '05-11-2026',
      interestRate: '2',
      repayment: 'emi',
      interestType: 'simple',
      handLoanType: 'personal',
    }),
  ];
  return `\uFEFF${rows.map((r) => r.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

export function bulkResultsCsv(rows: { rowNumber: number; name: string; status: string; message: string }[]): string {
  const lines = [['Sheet row', 'Customer', 'Result', 'Details'], ...rows.map((r) => [String(r.rowNumber), r.name, r.status, r.message])];
  return `\uFEFF${lines.map((r) => r.map(csvCell).join(',')).join('\r\n')}\r\n`;
}
